import {
  Palette as PaletteIcon,
  Check as CheckIcon,
  Brush as CustomIcon,
} from '@mui/icons-material';
import {
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  ListItemText,
  ListItemIcon,
  Chip,
  Box,
  Typography,
  Divider,
} from '@mui/material';
import React, { useState } from 'react';

import { useThemeMode } from '../../contexts/ThemeContext';

/**
 * ThemeSelector Component
 * 
 * Demonstrates the CSS variables-based theme system by providing
 * a dropdown to select from the 8 predefined professional themes.
 * 
 * Features:
 * - No re-renders when switching themes (uses CSS variables)
 * - Smooth transitions between theme changes
 * - Shows current theme with check mark
 * - Displays theme descriptions
 * - Supports both predefined and custom themes
 */
function ThemeSelector({ size = 'medium' }) {
  const { 
    selectedThemeIndex, 
    selectPredefinedTheme, 
    getCurrentThemeName,
    availableThemes,
    customTheme,
    isDarkMode, 
  } = useThemeMode();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeSelect = (themeIndex) => {
    selectPredefinedTheme(themeIndex);
    handleClose();
  };

  return (
    <>
      <Tooltip title="테마 선택">
        <IconButton
          onClick={handleClick}
          color="inherit"
          size={size}
          sx={{
            // Use CSS variables for consistent styling
            transition: 'all var(--theme-transitions-duration, 300ms) var(--theme-transitions-easing, ease-in-out)',
            backgroundColor: open ? 'var(--theme-palette-action-selected, rgba(0, 0, 0, 0.08))' : 'transparent',
            '&:hover': {
              backgroundColor: 'var(--theme-palette-action-hover, rgba(0, 0, 0, 0.04))',
              transform: 'scale(1.05)',
            },
            // Use CSS variables for dynamic colors
            color: 'var(--theme-palette-primary-main, #1976d2)',
          }}
        >
          <PaletteIcon />
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            // Use CSS variables for menu styling
            backgroundColor: 'var(--theme-palette-background-paper, #ffffff)',
            border: '1px solid var(--theme-palette-divider, rgba(0, 0, 0, 0.12))',
            borderRadius: 'var(--theme-shape-borderradius, 4px)',
            boxShadow: 'var(--theme-shadows-md, 0px 2px 4px rgba(0, 0, 0, 0.1))',
            minWidth: 280,
            maxWidth: 320,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600,
              color: 'var(--theme-palette-text-primary, rgba(0, 0, 0, 0.87))',
            }}
          >
            테마 선택
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'var(--theme-palette-text-secondary, rgba(0, 0, 0, 0.6))',
              display: 'block',
              mt: 0.5, 
            }}
          >
            현재: {getCurrentThemeName()} ({isDarkMode ? '다크' : '라이트'})
          </Typography>
        </Box>
        
        <Divider />
        
        {customTheme && (
          <>
            <MenuItem
              sx={{
                minHeight: 48,
                // Use CSS variables for hover effects
                '&:hover': {
                  backgroundColor: 'var(--theme-palette-action-hover, rgba(0, 0, 0, 0.04))',
                },
              }}
            >
              <ListItemIcon>
                <CustomIcon sx={{ color: 'var(--theme-palette-secondary-main, #dc004e)' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <span>사용자 정의 테마</span>
                    <CheckIcon 
                      fontSize="small" 
                      sx={{ color: 'var(--theme-palette-success-main, #4caf50)' }} 
                    />
                  </Box>
                }
                secondary="현재 적용된 커스텀 테마"
              />
            </MenuItem>
            <Divider />
          </>
        )}
        
        {availableThemes.map((theme, index) => {
          const isSelected = !customTheme && selectedThemeIndex === index;
          
          return (
            <MenuItem
              key={theme.name}
              onClick={() => handleThemeSelect(index)}
              sx={{
                minHeight: 56,
                // Use CSS variables for consistent styling
                backgroundColor: isSelected ? 'var(--theme-palette-action-selected, rgba(0, 0, 0, 0.08))' : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected 
                    ? 'var(--theme-palette-action-selected, rgba(0, 0, 0, 0.08))' 
                    : 'var(--theme-palette-action-hover, rgba(0, 0, 0, 0.04))',
                },
                transition: 'background-color var(--theme-transitions-duration, 300ms) ease-in-out',
              }}
            >
              <ListItemIcon>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${theme.chartColors[0]}, ${theme.chartColors[2]})`,
                    border: '2px solid var(--theme-palette-divider, rgba(0, 0, 0, 0.12))',
                    position: 'relative',
                  }}
                >
                  {isSelected && (
                    <CheckIcon 
                      sx={{ 
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontSize: 16,
                        color: '#fff',
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                      }} 
                    />
                  )}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? 600 : 400,
                        color: 'var(--theme-palette-text-primary, rgba(0, 0, 0, 0.87))',
                      }}
                    >
                      {theme.name}
                    </Typography>
                    {index < 3 && (
                      <Chip 
                        label="추천" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--theme-palette-text-secondary, rgba(0, 0, 0, 0.6))',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {theme.description}
                  </Typography>
                }
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default ThemeSelector;